import React from 'react';

/**
 * Generates initials from a full name
 * @param {string} name - The full name to generate initials from
 * @returns {string} - The initials in uppercase (e.g., "JS" for "John Smith")
 */
// export const getInitials = (name) => {
//   if (!name || typeof name !== 'string') {
//     return 'U';
//   }

//   return name
//     .trim()
//     .split(' ')
//     .filter(n => n.length > 0) // Remove empty strings
//     .map(n => n[0])
//     .join('')
//     .toUpperCase() || 'U';
// };

/**
 * Formats a name for display
 * @param {string} name - The name to format
 * @returns {string} - The formatted name or default fallback
 */
// export const formatDisplayName = (name) => {
//   if (!name || typeof name !== 'string') {
//     return 'User Name';
//   }
//   return name.trim() || 'User Name';
// };

/**
 * Parses message text and makes links clickable (Web version)
 * @param {string} text - The message text to parse
 * @param {object} textStyle - Optional style object for the text
 * @returns {React.ReactNode} - The parsed message with clickable links
 */
export const parseMessageWithLinks = (text, textStyle = {}) => {
  if (!text) return null;

  // Enhanced URL regex pattern that matches various URL formats
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.(?:com|org|net|edu|gov|mil|int|biz|info|name|museum|coop|aero|[a-z]{2})(?:\/[^\s]*)?)/g;

  // Split text by URLs and create an array of text parts and links
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }

    // Add the URL
    let url = match[0];
    // Add protocol if missing
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    parts.push({
      type: 'link',
      content: match[0],
      url: url
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  // If no URLs found, return the original text
  if (parts.length === 0) {
    return text;
  }

  // Render the parts with links as anchor tags
  return parts.map((part, index) => {
    if (part.type === 'link') {
      return (
        <a
          key={index}
          href={part.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: 'underline',
            color: '#007AFF',
            ...textStyle
          }}
          onClick={(e) => {
            // Let the browser handle the link opening
            console.log('Opening URL:', part.url);
          }}
        >
          {part.content}
        </a>
      );
    } else {
      return (
        <span key={index} style={textStyle}>
          {part.content}
        </span>
      );
    }
  });
};

/**
 * Opens a meeting link URL, handling different formats and ensuring proper protocol
 * @param {string|Array} url - The meeting URL (can be string or array)
 * @param {function} onError - Optional error callback function
 */
// export const openMeetingLink = (url, onError) => {
//   // Handle if URL is an array
//   let finalUrl = url;
//   if (Array.isArray(url)) {
//     finalUrl = url[0];
//   }

//   if (finalUrl && typeof finalUrl === 'string') {
//     // Ensure URL has protocol
//     const fullUrl = finalUrl.startsWith('http') ? finalUrl : `https://${finalUrl}`;
//     window.open(fullUrl, '_blank', 'noopener,noreferrer');
//   } else {
//     const error = new Error('Invalid meeting URL provided');
//     console.error('Invalid meeting URL:', url);
//     if (onError) {
//       onError(error);
//     }
//   }
// };

/**
 * Converts a mime type to a human readable extension.
 * @param {string} mimeType - e.g., 'image/jpeg' or 'application/pdf'
 * @returns {string}
 */
// export const deriveExtensionFromMime = (mimeType = '') => {
//   const lowerMime = `${mimeType}`.toLowerCase();

//   const extensionMap = {
//     // Images
//     'image/jpeg': 'jpg',
//     'image/png': 'png',
//     'image/gif': 'gif',
//     'image/webp': 'webp',
//     'image/svg+xml': 'svg',
//     // Documents
//     'application/pdf': 'pdf',
//     'text/plain': 'txt',
//     'text/csv': 'csv',
//     'application/msword': 'doc',
//     'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
//     'application/vnd.ms-excel': 'xls',
//     'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
//     // Archives
//     'application/zip': 'zip',
//     'application/vnd.rar': 'rar',
//     // Media
//     'video/mp4': 'mp4',
//     'video/quicktime': 'mov',
//     'audio/mpeg': 'mp3',
//     'audio/wav': 'wav'
//   };

//   return extensionMap[lowerMime] || 'bin'; // 'bin' is the standard fallback for unknown binaries
// };

/**
 * Determines if a message attachment is an image based on its MIME type.
 * @param {string} mimeType - e.g., 'image/jpeg' or 'application/pdf'
 * @returns {boolean}
 */
export const isImageFromMime = (mimeType = '') => {
  return `${mimeType}`.toLowerCase().startsWith('image/');
};

/**
 * Determines if a message attachment is an audio file based on its MIME type.
 * @param {string} mimeType - e.g., 'audio/mpeg' or 'image/jpeg'
 * @returns {boolean}
 */
export const isAudioFromMime = (mimeType = '') => {
  return `${mimeType}`.toLowerCase().startsWith('audio/');
};

/**
 * Convert timestamp to human readable time.
 * @param {string|number} timestamp - Unix timestamp (seconds or milliseconds) or date string
 * @returns {string}
 */
export const timeStampToDigital = (timestamp) => {
  if (!timestamp) return '';

  // Convert Unix timestamp to milliseconds if needed
  const timestampMs =
    timestamp.toString().length === 10
      ? timestamp * 1000 // Unix timestamp in seconds
      : timestamp; // Already in milliseconds

  return new Date(timestampMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Safely parses a JSON string.
 * @param {string} str - The JSON string to parse
 * @param {any} fallback - What to return if parsing fails (default: {})
 * @returns {any} The parsed object or the fallback
 */
export const safeJsonParse = (str, fallback = {}) => {
  if (!str) return fallback;

  try {
    if (typeof str === 'object') return str;
    return JSON.parse(str);
  } catch (e) {
    console.warn('Failed to parse JSON:', e);
    return fallback;
  }
};

/**
 * Converts bytes to a human-readable string (KB, MB, etc.)
 * @param {number} bytes - The total number of bytes
 * @param {number} decimals - How many decimal places to show (default 2)
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  // Determine which unit to use by calculating the log base 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Checks if a given date has passed compared to the current time.
 * @param {string | number | Date} targetDate - The date to check (ISO string, unix timestamp, or Date object).
 * @returns {boolean} True if the date is in the past (or invalid/missing), false if it is in the future.
 */
export const isExpired = (targetDate) => {
  // Treat missing or undefined dates as expired
  if (!targetDate) return true;

  const dateToCompare = new Date(targetDate);

  // Safety check: ensure the provided value can be parsed into a valid Date
  if (isNaN(dateToCompare.getTime())) {
    console.warn('Invalid date format passed to isExpired:', targetDate);
    return true;
  }

  const currentDate = new Date();

  return currentDate >= dateToCompare;
};

/**
 * Checks if a popup blocker is active.
 * @returns {boolean} True if a popup blocker is detected, false otherwise.
 */
export const isPopupBlockerEnabled = () => {
  let isBlocked = false;

  try {
    // Attempt to open a tiny, off-screen window
    const popup = window.open('about:blank', '_blank', 'width=1,height=1,left=-10000,top=-10000');

    // Check if the popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      isBlocked = true;
    } else {
      // If the popup opened successfully, close it immediately
      popup.close();
      isBlocked = false;
    }
  } catch (error) {
    // Strict blockers or browser security settings might throw an error
    isBlocked = true;
  }

  return isBlocked;
};

/**
 * Parses message timestamps from various formats into a Date object.
 * Handles Unix timestamps (seconds), Unix timestamp strings, and datetime strings (MySQL/ISO).
 * @param {string|number|null} timestamp - The timestamp to parse
 * @returns {Date|null} - A valid Date object or null if parsing fails
 */
export const parseMessageTimestamp = (timestamp) => {
  if (!timestamp) return null;

  let date;

  if (typeof timestamp === 'number') {
    // Unix timestamp in seconds - convert to milliseconds
    date = new Date(timestamp * 1000);
  } else if (typeof timestamp === 'string') {
    // Check if it's a numeric string (Unix timestamp)
    if (/^\d+$/.test(timestamp)) {
      date = new Date(parseInt(timestamp, 10) * 1000);
    } else {
      // MySQL datetime string or ISO string - parse directly
      date = new Date(timestamp);
    }
  }

  // Return null if the date is invalid
  return date && !isNaN(date.getTime()) ? date : null;
};

/**
 * Formats a date as a chat date divider label.
 * Returns "Today", "Yesterday", or formatted date like "Mon 15 Jan".
 * @param {Date} date - The date to format
 * @returns {string} - The formatted date label
 */
export const formatDateDivider = (date) => {
  if (!date || isNaN(date.getTime())) return '';

  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);

  const dateDay = date.toDateString();

  if (dateDay === today.toDateString()) {
    return 'Today';
  }

  if (dateDay === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // Format as "Mon 15 Jan"
  const dayName = date.toDateString().slice(0, 3);
  const dayNumber = date.getDate();
  const monthName = date.toLocaleString('default', { month: 'short' });

  return `${dayName} ${dayNumber} ${monthName}`;
};
